// One question about a key, one predicate — the facts both the ranking and the
// resolution read, so neither can spell one of them its own way. Exported for
// its siblings in this directory; two of them — `laneAdmits` and `modelActive` —
// also reach the barrel, because the model picker (`../model-picker-options.ts`,
// #5201) must offer exactly what the resolver would spend, and a second spelling of
// either would drift. Nothing else here does.
// See `../llm-credential.ts` for the file docblock.

import {
  BYOK_KEY_PRIORITY_DEFAULT,
  type BYOKKey,
  BYOKKeyKind,
  type BYOKKeyModel,
  modelLive,
} from '../../entities/byok';
import { RunnerOwnerKind as OwnerKind, type RunnerOwnerKind } from '../../entities/runner';
import { isLaneFencePurpose } from '../job-class-routing';
import { isFlatRateKey } from './flat-rate';

// Runtime defaults for the optional pool fields — an unset field on a legacy /
// hand-built key routes as "default priority, active, untagged" (DB rows always
// carry concrete values via the NOT NULL column defaults).
const keyActive = (key: BYOKKey): boolean => key.active ?? true;
export const keyPriority = (key: BYOKKey): number => key.priority ?? BYOK_KEY_PRIORITY_DEFAULT;

/** An earmarked (donated) key is OSS-restricted — usable only for public-repo work. */
export const keyEarmarked = (key: BYOKKey): boolean => key.earmark != null;

/** A purposed key is LANE-restricted (#1460) — usable only for its own lane. */
const keyPurposed = (key: BYOKKey): boolean => key.purpose != null;

/**
 * Does a purposed key's lane admit this task? BOTH sides must be a RECOGNISED
 * lane and they must be equal. Validating rather than comparing alone is what
 * keeps an unrecognised purpose inert: `'reviewww' === 'reviewww'` would
 * otherwise let a typo'd key be spent by an equally typo'd caller.
 */
export const laneAdmits = (
  key: Pick<BYOKKey, 'purpose'>,
  taskPurpose: string | undefined,
): boolean => isLaneFencePurpose(key.purpose) && key.purpose === taskPurpose;

/** Is this a personal key (owned by a specific member) rather than an org key? */
const keyPersonal = (key: BYOKKey): boolean => key.userId != null;

/**
 * A key's billing nature — its stamped {@link BYOKKey.kind} when set, else the
 * SAME URL inference `isFlatRateKey` applies at insert time (a legacy row never
 * classified reads exactly as it would have been stamped). See #775 / arc 101
 * slice 03. Read by the ToS guard in {@link isUsable} AND by the resolver's
 * subscription-before-metered ranking band (plan 102 slice 05) — one spelling.
 */
export const effectiveKeyKind = (key: BYOKKey): BYOKKeyKind =>
  key.kind ?? (isFlatRateKey(key) ? BYOKKeyKind.Subscription : BYOKKeyKind.Metered);

/**
 * Is `key` VISIBLE to the acting user? Org keys (`userId` null) are visible to
 * everyone in the account; a personal key is visible ONLY to its owner. With no
 * acting user (autonomous org work) only org keys are visible — a personal key is
 * never spent by a run no member initiated. The per-user provider cascade fence
 * (epic #635): a member never signs with another member's key.
 */
export function visibleToActor(key: BYOKKey, actingUserId: string | undefined): boolean {
  if (!keyPersonal(key)) return true;
  return actingUserId !== undefined && key.userId === actingUserId;
}

/**
 * A usable key must be active, provider-valid, verified, and NOT cooling down —
 * AND, if it is an earmarked (donated) key, the task's REPO must be public (the
 * donation fence, arc 101 slice 10 / slice 02) — AND, if it carries a `purpose`,
 * the task's own LANE must equal it (the lane fence, #1460: a key provisioned for
 * one lane cannot be spent by another; `taskPurpose` absent fails CLOSED, so an
 * un-plumbed caller cannot quietly unfence it) — AND, if it is a personal
 * `kind=subscription` key, this claim must be sealing for that user's own
 * `owner_kind=user` box (the ToS guard, arc 101 slice 03, `03-provider-
 * keys.md` #6). `cooled` is the live `key_cooldown:<keyId>` verdict (arc 101
 * slice 03): a key throttled/exhausted within its TTL is dropped so it steps
 * aside instead of re-ranking first. `ossEligible` defaults to `false`, so a
 * donated key is dropped unless the work is provably public (fail-safe).
 *
 * The ToS guard: a personal SUBSCRIPTION key is a flat-rate coding-plan
 * credential licensed to ONE individual — provider ToS bars rotating it across
 * a team/fleet. `visibleToActor` already fences it to its OWNER's own
 * dispatched work; this additionally fences WHICH BOX may spend it, since an
 * org/shared box can serve several members' work and would rotate a personal
 * subscription across machines the provider never authorized. `boxOwnerKind`
 * absent (no box context) fails CLOSED — same fail-safe posture as
 * `ossEligible`. An org-owned subscription key (`userId` null) is unaffected —
 * that account's own provider-ToS responsibility, not something this guard can
 * see (see `03-provider-keys.md`).
 */
export function isUsable(
  key: BYOKKey,
  ossEligible: boolean,
  cooled: boolean,
  boxOwnerKind: RunnerOwnerKind | undefined,
  taskPurpose: string | undefined,
): boolean {
  if (cooled) return false;
  if (!(keyActive(key) && key.status === 'valid' && key.verifiedAt !== null)) return false;
  if (keyEarmarked(key) && !ossEligible) return false;
  if (keyPurposed(key) && !laneAdmits(key, taskPurpose)) return false;
  if (
    keyPersonal(key) &&
    effectiveKeyKind(key) === BYOKKeyKind.Subscription &&
    boxOwnerKind !== OwnerKind.User
  ) {
    return false;
  }
  return true;
}

/**
 * How much each successive fallback tier decays below the primary tier match
 * (+10, +7, +4, +1…), so a reassignment walks the chain rather than an arbitrary
 * untagged key. Floors at 1, which keeps the whole chain inside
 * `MATCH_BAND_WEIGHTS.tierPrimary` — the bound the separation below relies on.
 */
export const TIER_CHAIN_DECAY = 3;

/**
 * The same decay for the MODEL-tier band (+30, +23, +16, +9…), scaled to that
 * band's own head so the whole chain stays inside `MATCH_BAND_WEIGHTS.modelTier`.
 * One decay per band rather than one shared constant: the separation invariant is
 * stated per band, so a chain that outgrew its own head would break the table's
 * ordering rather than merely rank oddly.
 */
export const MODEL_TIER_CHAIN_DECAY = 7;

/**
 * A tier tag's score within its band: the PRIMARY tier scores the band's head and
 * each later fallback decays toward the floor of 1, so a reassignment walks the
 * chain (heavy→smart→general) instead of landing on an arbitrary untagged key. The
 * floor keeps the whole chain inside `head`, which is what lets the band table
 * below reason about one number per band.
 */
export const tierChainScore = (idx: number, head: number, decay: number): number =>
  Math.max(1, head - idx * decay);

/** Runtime default for a catalogue entry's `active` — absent reads as on, like the key's own. */
export const modelActive = (m: Pick<BYOKKeyModel, 'active'>): boolean => m.active ?? true;

/**
 * The key's LIVE catalogue (`byok_key_models`) at `now` — parked entries, and entries
 * cooling past `now` behind a dated refusal ({@link modelLive}), are invisible to
 * resolution.
 */
export const activeModels = (key: BYOKKey, now: Date = new Date()): readonly BYOKKeyModel[] =>
  (key.models ?? []).filter((m) => modelLive(m, now));
